import { useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/shared/context/AuthContext";
import { api } from "@/shared/lib/api";
import {
  requestPushPermissionAndToken,
  onForegroundPush,
} from "@/shared/lib/firebase";

/**
 * Background hook that wires Firebase Cloud Messaging to the rider app.
 *
 *   1. On login, IF the browser has already granted Notification permission,
 *      silently fetch the FCM token and POST it to the backend.  We never
 *      auto-prompt — that's intrusive; the user opts in via a settings CTA.
 *   2. On any foreground message, show a toast (Stripe + Sonner pattern).
 *
 * Mount once near the top of the app tree.  Unmounting tears down the
 * onMessage listener cleanly.
 */
export default function PushNotificationsBootstrap() {
  const { user } = useAuth();

  // Token registration / re-registration whenever the user identity flips.
  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined") return;
    if (!("Notification" in window) || Notification.permission !== "granted") return;

    let cancelled = false;
    (async () => {
      const token = await requestPushPermissionAndToken();
      if (!token || cancelled) return;
      try {
        await api.post("/notifications/register-token", {
          token,
          platform: detectPlatform(),
        });
      } catch (e) {
        // Token registration failures are benign — backend may not yet
        // have the service-account credential, or the user signed out
        // mid-flight.  Log only, don't toast.
        console.warn("[fcm] register-token failed:", e?.message || e);
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  // Foreground push → toast.  Background pushes are handled by the
  // service worker (firebase-messaging-sw.js).
  useEffect(() => {
    let unsub = () => {};
    (async () => {
      unsub = await onForegroundPush((payload) => {
        const title = payload.notification?.title || payload.data?.title;
        const body = payload.notification?.body || payload.data?.body;
        if (!title && !body) return;
        toast(title || "Link", { description: body });
      });
    })();
    return () => { try { unsub(); } catch (_) {} };
  }, []);

  return null;
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "web";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "web";
}
